import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { AlertTriangle, BarChart3, Clock3, ExternalLink, ShieldCheck, Users, type LucideIcon } from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useOverrideUsageReportQuery, useRolloutReadinessReportQuery } from "@/api/query-hooks";
import type { RolloutReadinessStationRecord } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, ErrorState, LoadingState } from "@/components/layout/route-states";
import { ClientScopePicker, useTenantClientScope } from "@/features/licensing/client-scope";
import { ClientIdentity } from "@/features/licensing/client-identity";
import { formatDateTime, relativeDateTime } from "@/lib/date";
import { positiveIntegerSearch } from "@/lib/search";

type ReportsSearch = {
  appId?: string;
  clientId?: number;
  staleAfterHours?: string;
  since?: string;
  until?: string;
};

export function ReportsPage() {
  const { t, i18n } = useTranslation(["reports", "common"]);
  const navigate = useNavigate({ from: "/reports" });
  const search = useSearch({ from: "/reports" });
  const rolloutParams = useMemo(() => rolloutReportParams(search), [search]);
  const overrideParams = useMemo(() => overrideReportParams(search), [search]);
  const scope = useTenantClientScope(search.clientId);
  const rollout = useRolloutReadinessReportQuery(rolloutParams, scope.ready);
  const overrides = useOverrideUsageReportQuery(overrideParams, scope.ready);

  if (scope.isLoading || (scope.ready && (rollout.isLoading || overrides.isLoading))) {
    return <LoadingState />;
  }
  if (scope.isError) {
    return <ErrorState title={t("common:states.error")} detail={scope.error instanceof Error ? scope.error.message : undefined} onRetry={() => void scope.refetch()} />;
  }
  if (scope.ready && rollout.isError) {
    return <ErrorState title={t("common:states.error")} detail={rollout.error.message} onRetry={() => void rollout.refetch()} />;
  }
  if (scope.ready && overrides.isError) {
    return <ErrorState title={t("common:states.error")} detail={overrides.error.message} onRetry={() => void overrides.refetch()} />;
  }
  const readiness = !rollout.data || rollout.data.total === 0 ? 0 : Math.round((rollout.data.ready / rollout.data.total) * 100);

  return (
    <div className="grid gap-5">
      <div>
        <h2 className="text-lg font-semibold">{t("title")}</h2>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t("description")}</p>
      </div>
      {scope.tenant ? (
        <section className="rounded-md border bg-panel p-3">
          <ClientScopePicker
            value={search.clientId}
            required={scope.required}
            onChange={(clientId) => void navigate({ search: { ...search, clientId } })}
          />
        </section>
      ) : null}

      <section className="rounded-md border bg-panel p-3">
        <div className="grid gap-3 lg:grid-cols-[1fr_150px_150px_150px]">
          <Field label={t("filters.app")}>
            <Input
              value={search.appId ?? ""}
              placeholder="mailarchiver"
              onChange={(event) => void navigate({ search: { ...search, appId: event.target.value || undefined } })}
            />
          </Field>
          {!scope.tenant ? <Field label={t("filters.client")}><Input value={search.clientId ?? ""} inputMode="numeric" onChange={(event) => void navigate({ search: { ...search, clientId: positiveIntegerSearch(event.target.value) } })} /></Field> : null}
          <Field label={t("filters.staleAfter")}>
            <Input
              value={search.staleAfterHours ?? "48"}
              inputMode="numeric"
              onChange={(event) => void navigate({ search: { ...search, staleAfterHours: event.target.value || undefined } })}
            />
          </Field>
          <Field label={t("filters.since")}>
            <Input
              value={search.since ?? ""}
              placeholder="2026-05-01"
              onChange={(event) => void navigate({ search: { ...search, since: event.target.value || undefined } })}
            />
          </Field>
          <Field label={t("filters.until")}>
            <Input
              value={search.until ?? ""}
              placeholder="2026-06-01"
              onChange={(event) => void navigate({ search: { ...search, until: event.target.value || undefined } })}
            />
          </Field>
        </div>
      </section>

      {!rollout.data || !overrides.data ? <EmptyState title={t("common:states.empty")} /> : <><div className="grid gap-3 md:grid-cols-5">
        <Metric icon={ShieldCheck} label={t("metrics.ready")} value={`${rollout.data.ready}/${rollout.data.total}`} />
        <Metric icon={BarChart3} label={t("metrics.readiness")} value={`${readiness}%`} />
        <Metric icon={AlertTriangle} label={t("metrics.attention")} value={rollout.data.attention.length} tone={rollout.data.attention.length > 0 ? "warning" : "success"} />
        <Metric icon={Clock3} label={t("metrics.stale")} value={rollout.data.stale} tone={rollout.data.stale > 0 ? "warning" : "success"} />
        <Metric icon={Users} label={t("metrics.overrides")} value={overrides.data.total} tone={overrides.data.total > 0 ? "warning" : "default"} />
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.2fr_0.8fr]">
        <section className="rounded-md border bg-panel">
          <div className="border-b px-4 py-3">
            <h3 className="font-medium">{t("rollout.title")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("rollout.generated", { at: formatDateTime(rollout.data.generatedAt, i18n.language), summary: rollout.data.readinessSummary })}
            </p>
          </div>
          {rollout.data.attention.length === 0 ? (
            <div className="p-4">
              <Badge variant="success">{t("rollout.noAttention")}</Badge>
            </div>
          ) : (
            <StationsTable stations={rollout.data.attention} language={i18n.language} />
          )}
        </section>

        <section className="grid gap-5">
          <Coverage title={t("coverage.status")} values={rollout.data.byStatus} />
          <Coverage title={t("coverage.version")} values={rollout.data.byVersion} />
          <ClientCoverage title={t("coverage.client")} values={rollout.data.byClient} />
        </section>
      </div>

      <section className="rounded-md border bg-panel">
        <div className="border-b px-4 py-3">
          <h3 className="font-medium">{t("overrides.title")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("overrides.window", {
              since: formatDateTime(overrides.data.since, i18n.language),
              until: formatDateTime(overrides.data.until, i18n.language)
            })}
          </p>
        </div>
        {overrides.data.items.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">{t("overrides.empty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] border-collapse text-sm">
              <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t("columns.client")}</th>
                  <th className="px-3 py-2">{t("columns.station")}</th>
                  <th className="px-3 py-2">{t("columns.employee")}</th>
                  <th className="px-3 py-2">{t("columns.issuedAt")}</th>
                  <th className="px-3 py-2">{t("columns.status")}</th>
                  <th className="px-3 py-2">{t("columns.reason")}</th>
                </tr>
              </thead>
              <tbody>
                {overrides.data.items.map((item) => (
                  <tr key={item.id} className="border-t">
                    <td className="px-3 py-2"><ClientIdentity clientId={item.clientId} /></td>
                    <td className="px-3 py-2 font-mono text-xs">{item.stationId}</td>
                    <td className="px-3 py-2">{item.issuedByEmployeeId}</td>
                    <td className="px-3 py-2">{formatDateTime(item.issuedAt, i18n.language)}</td>
                    <td className="px-3 py-2"><Badge variant="outline">{item.status}</Badge></td>
                    <td className="max-w-md truncate px-3 py-2">{item.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      </>}
    </div>
  );
}

function rolloutReportParams(search: ReportsSearch) {
  const params = new URLSearchParams();
  if (search.appId) params.set("appId", search.appId);
  if (search.clientId) params.set("clientId", String(search.clientId));
  params.set("staleAfterHours", search.staleAfterHours || "48");
  return params;
}

function overrideReportParams(search: ReportsSearch) {
  const params = new URLSearchParams();
  if (search.appId) params.set("appId", search.appId);
  if (search.clientId) params.set("clientId", String(search.clientId));
  if (search.since) params.set("since", search.since);
  if (search.until) params.set("until", search.until);
  return params;
}

function StationsTable({ stations, language }: { stations: RolloutReadinessStationRecord[]; language: string }) {
  const { t } = useTranslation(["reports", "common"]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[980px] border-collapse text-sm">
        <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
          <tr>
            <th className="px-3 py-2">{t("columns.client")}</th>
            <th className="px-3 py-2">{t("columns.station")}</th>
            <th className="px-3 py-2">{t("columns.status")}</th>
            <th className="px-3 py-2">{t("columns.version")}</th>
            <th className="px-3 py-2">{t("columns.lastSeen")}</th>
            <th className="px-3 py-2">{t("columns.reasons")}</th>
            <th className="px-3 py-2 text-right">{t("common:actions.open")}</th>
          </tr>
        </thead>
        <tbody>
          {stations.map((station) => (
            <tr key={station.id} className="border-t">
              <td className="px-3 py-2"><ClientIdentity clientId={station.clientId} /></td>
              <td className="px-3 py-2">
                <div className="font-mono text-xs">{station.id}</div>
                <div className="text-xs text-muted-foreground">{station.hostname || `${station.os}/${station.arch}`}</div>
              </td>
              <td className="px-3 py-2"><Badge variant={station.status === "HEALTHY" ? "success" : "warning"}>{station.status}</Badge></td>
              <td className="px-3 py-2">{station.currentVersion || "—"}</td>
              <td className="px-3 py-2">
                <div>{relativeDateTime(station.lastSeenAt, language)}</div>
                <div className="text-xs text-muted-foreground">{formatDateTime(station.lastSeenAt, language)}</div>
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {(station.reasons ?? []).map((reason) => (
                    <Badge key={reason} variant="warning">{reason}</Badge>
                  ))}
                </div>
              </td>
              <td className="px-3 py-2 text-right">
                <Button asChild size="sm" variant="outline">
                  <Link to="/stations/$stationId" params={{ stationId: station.id }} search={{ recoveryRequestId: undefined }}>
                    <ExternalLink className="h-4 w-4" />
                    {t("common:actions.open")}
                  </Link>
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Coverage({ title, values }: { title: string; values: Record<string, number> }) {
  const entries = Object.entries(values).sort((a, b) => b[1] - a[1]);
  return (
    <section className="rounded-md border bg-panel p-4">
      <h3 className="font-medium">{title}</h3>
      <div className="mt-3 grid gap-2">
        {entries.length === 0 ? <div className="text-sm text-muted-foreground">—</div> : null}
        {entries.map(([key, value]) => (
          <div key={key} className="flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0 truncate">{key}</span>
            <Badge variant="outline">{value}</Badge>
          </div>
        ))}
      </div>
    </section>
  );
}

function ClientCoverage({ title, values }: { title: string; values: Record<string, number> }) {
  const entries = Object.entries(values).sort((a, b) => b[1] - a[1]);
  return (
    <section className="rounded-md border bg-panel p-4">
      <h3 className="font-medium">{title}</h3>
      <div className="mt-3 grid gap-2">
        {entries.length === 0 ? <div className="text-sm text-muted-foreground">—</div> : null}
        {entries.map(([key, value]) => (
          <div key={key} className="flex items-center justify-between gap-3 text-sm">
            <ClientIdentity clientId={positiveIntegerSearch(key)} compact />
            <Badge variant="outline">{value}</Badge>
          </div>
        ))}
      </div>
    </section>
  );
}

function Metric({ icon: Icon, label, value, tone = "default" }: { icon: LucideIcon; label: string; value: number | string; tone?: "default" | "success" | "warning" }) {
  return (
    <section className="rounded-md border bg-panel p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm text-muted-foreground">{label}</div>
          <div className="mt-1 text-2xl font-semibold">{value}</div>
        </div>
        <Icon className={tone === "warning" ? "h-5 w-5 text-warning" : tone === "success" ? "h-5 w-5 text-success" : "h-5 w-5 text-primary"} />
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
